'use client';

import React, { useEffect, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';

const LoadingScreen = () => (
  <div style={{
    minHeight: '100vh',
    background: '#0A0A0A',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'Montserrat, sans-serif',
    gap: '16px',
  }}>
    <div style={{ fontWeight: 900, fontSize: '22px', color: '#FFFFFF', letterSpacing: '0.08em' }}>
      Drive<span style={{ color: '#C8102E' }}>X</span>
    </div>
    <div style={{
      width: '28px', height: '28px',
      border: '3px solid rgba(200,16,46,0.2)',
      borderTopColor: '#C8102E',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
    }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

export default function AdminGuard({ children }: { children: React.ReactNode }) {
  const { user, profile, loading } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const [profileTimeout, setProfileTimeout] = useState(false);

  const isLoginPage = pathname === '/admin/login';

  // Timeout for profile loading — if profile doesn't load in 5s, redirect to login
  useEffect(() => {
    if (isLoginPage || loading || profile) return;
    if (!user) return;

    const timer = setTimeout(() => {
      setProfileTimeout(true);
    }, 5000);

    return () => clearTimeout(timer);
  }, [isLoginPage, loading, profile, user]);

  useEffect(() => {
    if (isLoginPage) return;
    if (loading) return;

    if (!user) {
      router.replace('/admin/login');
      return;
    }

    if (profile && profile.role === 'superadmin') {
      router.replace('/superadmin/tenants');
      return;
    }

    if (profileTimeout && !profile) {
      // Profile failed to load — sign out and redirect to login
      router.replace('/admin/login');
    }
  }, [user, profile, loading, router, isLoginPage, profileTimeout]);

  // Login page — render without guard
  if (isLoginPage) return <>{children}</>;

  if (loading) return <LoadingScreen />;
  if (!user) return null;

  // Profile still loading — show spinner (with timeout fallback above)
  if (!profile && !profileTimeout) return <LoadingScreen />;

  // Profile timed out or failed
  if (!profile) return null;

  // Superadmin trying to access admin — redirect handled in useEffect
  if (profile.role === 'superadmin') return null;

  return <>{children}</>;
}
